import { Link } from 'react-router-dom';

const featuredGuides = [
  {
    title: 'Ultimate Superbuy Spreadsheet Guide',
    description: 'Everything you need to know about finding, verifying, and buying the best fashion items through Superbuy Spreadsheet. A complete walkthrough for beginners and experienced shoppers alike.',
    slug: '/superbuy-spreadsheet-guide',
    imageUrl: 'https://readdy.ai/api/search-image?query=Artistic%20minimalist%20editorial%20photography%20of%20an%20open%20laptop%20with%20spreadsheet%20displayed%20alongside%20curated%20fashion%20items%20on%20warm%20cream%20desk%20surface%2C%20soft%20natural%20window%20lighting%2C%20sophisticated%20modern%20workspace%20aesthetic%2C%20neutral%20beige%20and%20oatmeal%20tones%2C%20clean%20Scandinavian%20interior%2C%20lifestyle%20tech%20and%20fashion%20fusion%20composition&width=800&height=500&seq=guide-main-v2-july-2026&orientation=landscape',
  },
  {
    title: 'Shoes Shopping Guide',
    description: 'From sneakers to boots, discover how to use the Superbuy Spreadsheet to find the best footwear deals across all categories and brands.',
    slug: '/superbuy-spreadsheet-shoes-guide',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20photography%20of%20premium%20sneakers%20beautifully%20arranged%20on%20warm%20cream%20surface%20with%20soft%20natural%20lighting%2C%20editorial%20footwear%20display%2C%20neutral%20beige%20and%20sand%20tones%2C%20sophisticated%20product%20styling%2C%20modern%20minimal%20composition%2C%20clean%20aesthetic%20with%20gentle%20shadows&width=800&height=500&seq=guide-shoes-v2-july-2026&orientation=landscape',
  },
  {
    title: 'Hoodies & Streetwear Finds',
    description: 'Explore the hottest hoodies and streetwear pieces curated in our Superbuy Spreadsheet. Updated with the latest drops and seasonal collections.',
    slug: '/superbuy-spreadsheet-hoodies-guide',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20premium%20heavyweight%20hoodies%20in%20neutral%20tones%20draped%20on%20warm%20cream%20surface%2C%20soft%20diffused%20natural%20light%2C%20editorial%20streetwear%20styling%2C%20beige%20and%20oatmeal%20palette%2C%20sophisticated%20casual%20aesthetic%2C%20modern%20minimal%20composition%20with%20rich%20fabric%20texture%20visible&width=800&height=500&seq=guide-hoodies-v2-july-2026&orientation=landscape',
  },
];

export default function SEOContent() {
  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide uppercase mb-4">
              About Superbuy Sheet
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
              Your Trusted Fashion Shopping Spreadsheet Hub
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                A <strong>Superbuy Spreadsheet</strong> is the smartest way to discover and track fashion finds
                from global marketplaces. Whether you&apos;re hunting for the latest sneaker drops, premium hoodies,
                luxury bags, or budget-friendly streetwear, a well-maintained Superbuy Spreadsheet gives you instant
                access to thousands of verified product links, QC photos, price comparisons, and shipping estimates
                — all organized in one place.
              </p>
              <p>
                Our <strong>Superbuy Spreadsheet</strong> is updated daily with fresh finds across 14 categories
                including shoes, hoodies, jackets, bags, accessories, and more. Each entry is hand-picked and
                verified by experienced shoppers who understand what quality looks like. We track prices, monitor
                seller reliability, and update links regularly so you never waste time on dead listings or
                unreliable vendors.
              </p>
              <p>
                Unlike random spreadsheets floating around forums, our <strong>Superbuy Spreadsheet</strong> follows
                a structured approach: every product category is clearly organized, every link is tested, and we
                provide helpful buying guides alongside the spreadsheet to help you navigate the entire shopping
                process — from finding the right item to understanding QC photos, choosing shipping lines, and
                calculating total costs.
              </p>
              <p>
                The beauty of a <strong>Superbuy Spreadsheet</strong> is its simplicity. No complicated apps,
                no membership fees, no hidden charges. Just open the spreadsheet, browse by category, find what
                you love, and click through to purchase. It&apos;s the most efficient way to shop across multiple
                platforms while keeping everything organized and trackable.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/superbuy-spreadsheet-guide"
                className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all hover:-translate-y-0.5 cursor-pointer"
              >
                Read Full Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-medium hover:border-foreground-400 hover:bg-background-50 transition-all cursor-pointer"
              >
                Open kaspreadsheet.com
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>

          <div className="bg-primary-700 rounded-3xl p-6 md:p-8 text-background-50">
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs font-medium text-primary-200 tracking-wide uppercase">Why Trust Us</span>
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-background-50/30"></span>
                <span className="w-2 h-2 rounded-full bg-background-50/60"></span>
                <span className="w-2 h-2 rounded-full bg-background-50/30"></span>
              </div>
            </div>
            <blockquote className="text-lg md:text-xl font-heading font-light italic leading-relaxed mb-6">
              &ldquo;We curate every link, verify every seller, and update our Superbuy Spreadsheet daily so you can
              shop with confidence. No guesswork, no dead links, no wasted time — just the best fashion finds
              organized and ready for you.&rdquo;
            </blockquote>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-background-50 font-bold text-sm">
                  SB
                </div>
                <div>
                  <div className="text-sm font-semibold">Superbuy Spreadsheet Team</div>
                  <div className="text-xs text-primary-200">Curated & Verified Daily</div>
                </div>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-4 py-2 rounded-full bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-all cursor-pointer flex items-center gap-1.5"
              >
                Browse Now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
              Curated Guides
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              For Smart Shoppers
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {featuredGuides.map((guide) => (
              <Link
                key={guide.title}
                to={guide.slug}
                className="group block rounded-2xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer"
              >
                <div className="relative w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={guide.imageUrl}
                    alt={guide.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                    {guide.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                    {guide.description}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary-600">
                    <span>2026 Update</span>
                    <span className="text-foreground-300 mx-1">·</span>
                    <span>SEO Article</span>
                    <i className="ri-arrow-right-line ml-auto"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/superbuy-spreadsheet-bags-guide"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-all cursor-pointer mr-3 mb-3"
            >
              Bags Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/superbuy-spreadsheet-accessories-guide"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-all cursor-pointer mb-3"
            >
              Accessories Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}